import { FormEvent, useState } from 'react';
import { useAuth } from './AuthProvider';
import { useI18n } from '../i18n/I18nProvider';
import { track } from '../lib/analytics';

function initialMode(): 'signin' | 'signup' {
  return new URLSearchParams(window.location.search).get('auth') === 'signup'
    ? 'signup'
    : 'signin';
}

function knownLeadEmail() {
  try {
    const lead = JSON.parse(window.localStorage.getItem('orivon_lead_v1') || 'null');
    return typeof lead?.email === 'string' ? lead.email : '';
  } catch {
    return '';
  }
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { locale, m } = useI18n();
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState(knownLeadEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  if (auth.loading) {
    return (
      <main className="auth-screen">
        <div className="loading-mark" aria-hidden="true" />
        <p>{m.common.loading}</p>
      </main>
    );
  }

  if (auth.user) return <>{children}</>;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || password.length < 8) {
      setError(m.auth.invalid);
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signin') {
        track('login_started', { language: locale });
        await auth.signIn(email.trim().toLowerCase(), password);
        track('login_completed', { language: locale });
      } else {
        track('signup_started', { language: locale });
        const status = await auth.signUp(email.trim().toLowerCase(), password, locale);
        track('account_created', { language: locale, confirmation_required: status === 'pending' });
        if (status === 'pending') setNotice(m.auth.confirmation);
      }
    } catch (cause) {
      track(mode === 'signin' ? 'login_failed' : 'signup_failed', { language: locale });
      setError(cause instanceof Error ? cause.message : m.auth.unavailable);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setError('');
    setNotice('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(m.auth.invalid);
      return;
    }
    setBusy(true);
    try {
      await auth.resetPassword(email.trim().toLowerCase(), locale);
      setNotice(m.auth.resetSent);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : m.auth.unavailable);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-screen">
      <a className="brand auth-brand" href={locale === 'it' ? '/it/' : '/'} aria-label="Orivon home">
        <img src="/favicon.svg" alt="" />
        <span>Orivon</span>
      </a>
      <section className="auth-card">
        <div className="eyebrow">{m.common.appName}</div>
        <h1>{mode === 'signup' ? m.auth.signupTitle : m.auth.title}</h1>
        <p>{mode === 'signup' ? m.auth.signupSubtitle : m.auth.subtitle}</p>
        {!auth.configured ? <div className="notice error">{m.auth.unavailable}</div> : null}
        <form onSubmit={submit}>
          <label>
            {m.auth.email}
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={!auth.configured || busy}
            />
          </label>
          <label>
            {m.auth.password}
            <input
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={!auth.configured || busy}
            />
          </label>
          {error ? <div className="notice error" role="alert">{error}</div> : null}
          {notice ? <div className="notice success" role="status">{notice}</div> : null}
          <button className="button primary" type="submit" disabled={!auth.configured || busy}>
            {busy ? m.common.loading : mode === 'signin' ? m.auth.signIn : m.auth.create}
          </button>
        </form>
        <button
          className="text-button"
          type="button"
          onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
        >
          {mode === 'signin' ? m.auth.switchCreate : m.auth.switchSignIn}
        </button>
        {mode === 'signin' ? (
          <button className="text-button subtle" type="button" disabled={busy} onClick={reset}>
            {m.auth.forgot}
          </button>
        ) : null}
      </section>
    </main>
  );
}
